import { Box, Grid2 as Grid, Typography } from '@mui/material';
import { FormattedTextField, DateFieldWithPrefix, createTruncatedTextFormat } from 'framework/ui/field';
import { useEffect } from 'react';
import { DATE_FORMAT } from 'framework/utils';
import { EditableListDataGridLayout, EDITABLE_LIST_FIELD, ListFiltersLayout } from 'framework/list';
import ProjectSectionAccordion from '../../ProjectSectionAccordion';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import {
  CloseFormShowButton,
  Dropdown,
  EditFormShowButton,
  SaveFormShowButton,
  TextInput,
  DateInput,
  useFormShowContext,
  useFormContext,
} from 'framework/form';
import { useEmployeeNamesByAliases } from 'domain/shared/projectHeatmap/useEmployeeNamesByAliases';
import { buildEmployeeFullName } from 'domain/shared/projectHeatmap/employeeName';
import { getVisibleHeatmapTeamMembers } from 'domain/shared/projectHeatmap/helpers';
import ProjectTeamMemberMultiSelectFilter from 'domain/shared/projectHeatmap/filter/ProjectTeamMemberMultiSelectFilter';
import { useProjectDataContext } from '../../context/ProjectDataContext';
import { useProjectSecurityContext } from '../../context/ProjectSecurityContext';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS } from '../../constants';
import ListSortButton from '../../ListSortButton';
import CalendarPrefixIcon from '../field/CalendarPrefixIcon';

const ProjectPlannedLeavesList = () => {
  const { data: project } = useProjectDataContext();
  const { isEditMode } = useFormShowContext();
  const { validationSchema, validateForm = () => {} } = useFormContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const visibleTeamMembers = getVisibleHeatmapTeamMembers(project.team?.members || []);

  const canManagePlannedLeaves = isAccessGranted(ACCESS_INFO_SECTIONS.TEAM_PLANNED_LEAVE, ACCESS_INFO_ACTIONS.MANAGE);
  const aliases = visibleTeamMembers.map(member => member.employeeAlias).filter(Boolean);
  const namesByAlias = useEmployeeNamesByAliases(aliases as string[]);
  const employeeOptions = visibleTeamMembers
    .flatMap(({ employeeAlias }) =>
      employeeAlias
        ? [{ value: employeeAlias, label: buildEmployeeFullName(employeeAlias, namesByAlias[employeeAlias]) }]
        : [],
    )
    .sort((left, right) => left.label.localeCompare(right.label));
  const employeeOptionsDepsKey = employeeOptions.map(option => `${option.value}:${option.label}`).join('|');

  useEffect(() => {
    if (isEditMode && validationSchema) {
      validateForm();
    }
  }, [isEditMode, validationSchema, validateForm]);

  const renderActions = () => {
    if (!canManagePlannedLeaves) return null;

    return isEditMode
      ? [<SaveFormShowButton key="save" />, <CloseFormShowButton key="close" />]
      : [<EditFormShowButton key="edit" />];
  };

  const getTableCells = async (isEditModeLayout: boolean) =>
    Promise.resolve([
      ...(isEditModeLayout
        ? [
            {
              type: EDITABLE_LIST_FIELD,
              source: 'employeeAlias',
              isEditor: true,
              component: Dropdown,
              componentProps: {
                options: employeeOptions,
                label: 'Team member',
                placeholder: 'Select team member',
              },
              gridCellProps: { size: { xs: 12, md: 3 } },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'startDate',
              isEditor: true,
              component: DateInput,
              gridCellProps: { size: { xs: 6, md: 1.5 } },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'endDate',
              isEditor: true,
              component: DateInput,
              gridCellProps: { size: { xs: 6, md: 1.5 } },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'comment',
              isEditor: true,
              component: TextInput,
              componentProps: { label: 'Comment' },
              gridCellProps: { size: { xs: 12, md: 5 } },
            },
          ]
        : [
            {
              type: EDITABLE_LIST_FIELD,
              source: 'employeeAlias',
              isEditor: false,
              component: EmployeeLinkFieldWithNameLoading,
              componentProps: {
                sx: { mb: 2 },
                ignoredNameParts: ['middleName', 'maidenName'],
              },
              gridCellProps: {
                size: 3,
              },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'startDate',
              isEditor: false,
              component: DateFieldWithPrefix,
              componentProps: {
                tooltipProps: {
                  title: 'Start',
                  children: <CalendarPrefixIcon />,
                },
                dateFormat: DATE_FORMAT.FULL_DATE,
              },
              gridCellProps: {
                size: 1.5,
              },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'endDate',
              isEditor: false,
              component: DateFieldWithPrefix,
              componentProps: {
                tooltipProps: {
                  title: 'End',
                  children: <CalendarPrefixIcon />,
                },
                dateFormat: DATE_FORMAT.FULL_DATE,
              },
              gridCellProps: {
                size: 1.5,
              },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'comment',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                format: createTruncatedTextFormat(40),
              },
              gridCellProps: {
                size: 6,
              },
            },
          ]),
    ]);

  return (
    <ProjectSectionAccordion
      defaultExpanded
      title={
        <Grid container direction="row" justifyContent="space-between" alignItems="center" pl={2} width="100%">
          <Grid size={2.3}>
            <Typography variant="h4" color="primary">
              Leave planning
            </Typography>
          </Grid>

          <Grid size={0.7}>
            <ListSortButton
              buttonId="planned-leaves-sort-button"
              options={[{ source: 'startDate', label: 'Start date' }]}
            />
          </Grid>

          <Grid
            size="auto"
            alignItems="center"
            sx={{ '.filter-item-wrapper': { mb: 0 }, '.filters-reset-btn': { mb: 0 } }}
          >
            <ListFiltersLayout>
              <ProjectTeamMemberMultiSelectFilter source="employeeAlias" label="Worker" members={visibleTeamMembers} />
            </ListFiltersLayout>
          </Grid>

          <Grid size="grow" />

          <Grid size="auto" display="flex" alignItems="center">
            {renderActions()}
          </Grid>
        </Grid>
      }
    >
      <Box sx={isEditMode ? { overflow: 'visible' } : { overflowX: 'auto' }}>
        <Box sx={isEditMode ? { width: '100%' } : { minWidth: '1000px' }}>
          <EditableListDataGridLayout
            addNewText="Add planned leave"
            getRowEnabled={() => true}
            rowProps={
              isEditMode
                ? { alignItems: 'flex-start', sx: { flexWrap: 'wrap' } }
                : { alignItems: 'center', sx: { py: 1 } }
            }
            getTableCells={getTableCells}
            rerenderTableCellsDeps={[employeeOptionsDepsKey]}
          />
        </Box>
      </Box>
    </ProjectSectionAccordion>
  );
};

export default ProjectPlannedLeavesList;
